import type { ArgumentsType } from "@vueuse/core";
import { getEmptyOrganization, Organization, OrganizationPath } from "../../../functions/src/store/organization";
import { getEmptyUser, User as OrganizationUser, UserPath } from "../../../functions/src/store/user";
import { type FirebaseApp, initializeApp } from "firebase/app";
import {
  getAuth,
  type Auth,
  type User,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  signInWithEmailAndPassword,
  signInWithCustomToken,
} from "firebase/auth";
import {
  getFirestore,
  Firestore,
  query,
  collection,
  doc,
  DocumentReference,
  CollectionReference,
  type FirestoreDataConverter,
  Timestamp,
  getDoc,
  getDocs,
  onSnapshot,
  type Unsubscribe,
  QueryFieldFilterConstraint,
  QueryOrderByConstraint,
  QueryConstraint,
  type QueryFilterConstraint,
  QueryCompositeFilterConstraint,
  QueryLimitConstraint,
  QueryStartAtConstraint,
  QueryEndAtConstraint,
  limit,
  orderBy,
  documentId,
  where,
  and,
  type QueryNonFilterConstraint,
  type OrderByDirection,
  QueryDocumentSnapshot,
  type DocumentChangeType,
  type WhereFilterOp,
  getDocFromCache,
} from "firebase/firestore";
import {
  getFunctions,
  type Functions,
  httpsCallable,
  type HttpsCallableResult,
  connectFunctionsEmulator,
} from "firebase/functions";
import { getDownloadURL, type StorageReference } from "firebase/storage";
import _, { cloneDeep, cond, defaultsDeep, get, isEqual, last, mapValues, maxBy, minBy, values } from "lodash";
import { DateTime } from "luxon";
import {
  computed,
  type ComputedRef,
  onMounted,
  onUnmounted,
  onUpdated,
  reactive,
  type Ref,
  ref,
  type UnwrapNestedRefs,
} from "vue";
import type { DocumentType } from "../../../functions/src/store/paper/base-paper";

interface Config {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId: string;
}

class FirebaseUtility {
  private app: FirebaseApp;
  public firestore: Firestore;
  public _functions: Functions;
  public auth: Auth;
  public organizationUser = ref<OrganizationUser>();
  public organization = ref<Organization>();
  public unwatch?: Unsubscribe;
  public claims: Omit<OrganizationUser, "updatedAt" | "createdAt" | "deletedAt" | "name">;
  protected initializePromise: Promise<void>;
  public get user(): User | null {
    return this.auth.currentUser;
  }

  public get logined(): Promise<boolean> {
    return (async () => {
      await this.initialize();
      if (!this.auth.currentUser) {
        return false;
      }
      if (this.auth.currentUser.isAnonymous) {
        this.logout();
        return false;
      }
      const claims = (await this.auth.currentUser.getIdTokenResult()).claims;
      if (!claims.organizationId) {
        this.logout();
        return false;
      }
      this.claims = claims as any;

      await this.loadData();
      return true;
    })();
  }

  public async reloadData() {
    this.auth = getAuth(this.app);
    await this.auth.currentUser?.getIdToken();
    await this.logined;
    await this.loadData();
  }

  private async loadData() {
    if (!this.auth.currentUser) return;
    const FirestoreOrganizationUser = new BaseFirestore(...UserPath);
    const organizationUserRef = FirestoreOrganizationUser.getDocument({
      organizationId: this.claims.organizationId,
      userId: this.auth.currentUser.uid,
    });
    const FirestoreOrganization = new BaseFirestore(...OrganizationPath);
    const organizationRef = FirestoreOrganization.getDocument({ organizationId: this.claims.organizationId });

    // this.organizationUser.value = (await getDoc(organizationUserRef)).data();

    let unwatchUser: Unsubscribe | null = null;
    let unwatchOrganization: Unsubscribe | null = null;
    try {
      this.unwatch?.();
      this.unwatch = undefined;
      unwatchUser = onSnapshot(organizationUserRef, async (querySnapshot) => {
        const newUserData = querySnapshot.data();
        if (!isEqual(this.organizationUser.value, newUserData)) {
          this.organizationUser.value = newUserData;
          try {
            await this.user?.getIdToken(true);
          } catch (e) {
            console.error(e);
          }
        }
      });
      unwatchOrganization = onSnapshot(organizationRef, async (querySnapshot) => {
        this.organization.value = querySnapshot.data();
      });
      const _organizationUser = (await getDoc(organizationUserRef)).data();
      defaultsDeep(_organizationUser, getEmptyUser());

      this.organizationUser.value = _organizationUser;

      const _organization = (await getDoc(organizationRef)).data();
      defaultsDeep(_organization, getEmptyOrganization());
      this.organization.value = _organization;

      this.unwatch = () => {
        unwatchUser?.();
        unwatchOrganization?.();
      };
    } catch (e) {
      unwatchUser?.();
      unwatchOrganization?.();
      throw e;
    }
  }

  constructor(protected config: Config) {
    this.app = initializeApp(config);
    this.firestore = getFirestore(this.app);
    this.auth = getAuth(this.app);
    this._functions = getFunctions(this.app, "asia-northeast1");
    if (IS_DEVELOPMENT_MODE) {
      (async () => {
        try {
          const projectId = this.app.options.projectId;
          const res = await fetch(`http://localhost:5001/${projectId}/asia-northeast1/ping`);
          if (res.status === 200) {
            console.log("functions emulator connected");
            connectFunctionsEmulator(this._functions, "localhost", 5001);
            return;
          }
        } catch (e) {}
      })();
    }

    this.initializePromise = this._initialize();
  }

  public async initialize(): Promise<void> {
    return this.initializePromise;
  }
  public async _initialize(): Promise<void> {
    return new Promise<void>((resolve) => {
      const unsubscribe = this.auth.onAuthStateChanged(async (user) => {
        unsubscribe();
        resolve();
      });
    });
  }

  async func<RES>(name: string, data: any): Promise<HttpsCallableResult<RES>> {
    const callable = httpsCallable<string, RES>(this._functions, name);
    return await callable(JSON.stringify(data));
  }
  async loginEmailPassword(email: string, password: string) {
    await signInWithEmailAndPassword(this.auth, email, password);
  }
  async loginWithCustomToken(token: string) {
    await signInWithCustomToken(this.auth, token);
  }
  async loginWithGoogle() {
    await signInWithPopup(this.auth, new GoogleAuthProvider());
  }
  async adminLoginEmailPassword(email: string, password: string) {
    await signInWithEmailAndPassword(this.auth, email, password);

    const claims = (await this.auth.currentUser?.getIdTokenResult())?.claims;
    if (claims?.admin) {
      return;
    }
    await this.logout();
  }

  async logout() {
    await signOut(this.auth);
  }

  getPaperPermission(type: DocumentType, permissionType: "read" | "write" | "submit") {
    if (!this.organizationUser.value) return "none";
    if (!this.user) return "none";
    return this.organizationUser.value[type][permissionType];
  }
  getCreatePaperPermissionAny() {
    return (
      this.getCreatePaperPermission("quotation") ||
      this.getCreatePaperPermission("invoice") ||
      this.getCreatePaperPermission("deliveryNote")
    );
  }
  getCreatePaperPermission(type: DocumentType) {
    if (!this.organization.value) return false;
    if (this.organization.value.plan != "trial" && this.organization.value.plan != "paid") return false;
    if (!this.organizationUser.value) return false;
    if (!this.user) return false;

    const permission = this.getPaperPermission(type, "write");
    if (permission === "none") return false;
    if (permission === "all") return true;
    if (permission === "assigned") return true;
    return false;
  }
  getEditPaperPermission(type: DocumentType, assignee: string[]) {
    if (!this.organization.value) return false;
    if (this.organization.value.plan != "trial" && this.organization.value.plan != "paid") return false;
    if (!this.organizationUser.value) return false;
    if (!this.user) return false;
    const permission = this.getPaperPermission(type, "write");
    if (permission === "none") return false;
    if (permission === "all") return true;
    if (permission === "assigned" && assignee.includes(this.user.uid)) return true;
    return false;
  }
  getSendPaperPermission(type: DocumentType, assignee: string[]) {
    if (!this.organization.value) return false;
    if (this.organization.value.plan != "trial" && this.organization.value.plan != "paid") return false;
    if (!this.organizationUser.value) return false;
    if (!this.user) return false;
    const permission = this.getPaperPermission(type, "submit");
    if (permission === "none") return false;
    if (permission === "all") return true;
    if (permission === "assigned" && assignee.includes(this.user.uid)) return true;
    return false;
  }
}

export const Firebase = new FirebaseUtility(FIREBASE_CONFIG);

export class BaseFirestoreData {
  updatedAt: DateTime;
  createdAt: DateTime;
  deletedAt: DateTime | null;
}

export class BaseFirestorePath {
  name?: string;
  path: string;
  primaryKey: string;
  param: typeof PathParam;
  class: typeof BaseFirestoreData;
  isCollection?: boolean;
  isPrivate?: boolean;
}

export class PathParam {
  __dummy?: string;
}

export const converter: FirestoreDataConverter<any, any> = {
  toFirestore: (_data) => {
    const data = cloneDeep(_data);
    toFirestoreConvert(data);
    return data;
  },
  fromFirestore: (snapshot, options) => {
    const data = snapshot.data(options);
    fromFirestoreConvert(data);
    return data;
  },
};

function toFirestoreConvert(obj: any) {
  for (const key in obj) {
    if (!obj[key]) {
      continue;
    } else if (obj[key] instanceof DateTime) {
      obj[key] = obj[key].toJSDate();
    } else if (typeof obj[key] === "object" && "toJSDate" in obj[key]) {
      obj[key] = obj[key].toJSDate();
    } else if (typeof obj[key] === "object") {
      toFirestoreConvert(obj[key]);
    } else if (typeof obj[key] === "function") {
      toFirestoreConvert(obj[key]);
    }
  }
}

function fromFirestoreConvert(obj: any) {
  for (const key in obj) {
    if (!obj[key]) {
      continue;
    } else if (obj[key] instanceof Timestamp) {
      obj[key] = DateTime.fromJSDate(obj[key].toDate());
    } else if (typeof obj[key] === "object") {
      fromFirestoreConvert(obj[key]);
    } else if (typeof obj[key] === "function") {
      fromFirestoreConvert(obj[key]);
    }
  }
}

export class BaseFirestore<TCol extends BaseFirestorePath, TDoc extends BaseFirestorePath> {
  constructor(
    public collectionPath: TCol,
    public documentPath: TDoc
  ) {}
  getDocument(
    param: TDoc["param"]["prototype"]
  ): DocumentReference<TDoc["class"]["prototype"], TDoc["class"]["prototype"]> {
    return doc(Firebase.firestore, getPathFromParam(this.documentPath.path, param)).withConverter(converter);
  }
  getCollection(
    param: TCol["param"]["prototype"]
  ): CollectionReference<TCol["class"]["prototype"], TCol["class"]["prototype"]> {
    return collection(Firebase.firestore, getPathFromParam(this.collectionPath.path, param)).withConverter(converter);
  }
}

export function getPathFromParam(path: string, param: Object) {
  for (const key in param) {
    const p = (param as any)[key];
    if (p) {
      path = path.replace(new RegExp("{" + key + "}", "g"), p);
    }
  }
  if (path.search(/[{}]/) >= 0) {
    console.error(path, param);
  }

  return path;
}

export function constraintsToQuery<TCol extends BaseFirestorePath, TDoc extends BaseFirestorePath>(
  tcol: TCol,
  tdoc: TDoc,
  params: TCol["param"]["prototype"],
  constraint: (QueryConstraint | QueryFilterConstraint)[]
) {
  const firestoreBase = new BaseFirestore(tcol, tdoc);
  const firestoreRef = firestoreBase.getCollection(params);

  const hasQueryCompositeFilter = constraint.some((i) => i instanceof QueryCompositeFilterConstraint);
  const _queryCompositeFilterConstraint = constraint.filter(
    (i) => i instanceof QueryCompositeFilterConstraint || i instanceof QueryFieldFilterConstraint
  );
  const _queryNonFilterConstraint = constraint.filter(
    (i) =>
      i instanceof QueryOrderByConstraint ||
      i instanceof QueryLimitConstraint ||
      i instanceof QueryStartAtConstraint ||
      i instanceof QueryEndAtConstraint
  );

  const FirestoreQuery = hasQueryCompositeFilter
    ? query(
        firestoreRef,
        and(...(_queryCompositeFilterConstraint as QueryFilterConstraint[])),
        ...(_queryNonFilterConstraint as QueryNonFilterConstraint[])
      )
    : query(firestoreRef, ...(constraint as QueryConstraint[]));
  return FirestoreQuery;
}

export async function getFirestoreDoc<
  TCol extends BaseFirestorePath,
  TDoc extends BaseFirestorePath,
  TServiceRequiredMethods extends {
    load(): Promise<void>;
    reload(data: TDoc["class"]["prototype"]): Promise<void>;
  },
  TService extends {
    new (
      docRef: DocumentReference<TDoc["class"]["prototype"], TDoc["class"]["prototype"]>,
      data: TDoc["class"]["prototype"]
    ): InstanceType<TService> & TServiceRequiredMethods;
  },
>(
  tcol: TCol,
  tdoc: TDoc,
  service: TService,
  _params: () => TDoc["param"]["prototype"]
): Promise<InstanceType<TService> | null> {
  const FirestoreBase = new BaseFirestore(tcol, tdoc);

  await Firebase.initialize();
  const FirestoreRef = FirestoreBase.getDocument(_params());
  const doc = await getDoc(FirestoreRef);

  const data = doc.data();
  if (!data) {
    return null;
  }
  const s = new service(doc.ref, data);
  await s.load();
  return s;
}

export async function getFirestoreCol<
  TCol extends BaseFirestorePath,
  TDoc extends BaseFirestorePath,
  TServiceRequiredMethods extends {
    load(): Promise<void>;
    reload(data: TDoc["class"]["prototype"]): Promise<void>;
  },
  TService extends {
    new (
      docRef: DocumentReference<TDoc["class"]["prototype"], TDoc["class"]["prototype"]>,
      data: TDoc["class"]["prototype"]
    ): InstanceType<TService> & TServiceRequiredMethods;
  },
>(
  tcol: TCol,
  tdoc: TDoc,
  service: TService,
  _params: () => TCol["param"]["prototype"],
  _querys: () => (QueryConstraint | QueryFilterConstraint)[] = () => []
): Promise<InstanceType<TService>[]> {
  const FirestoreBase = new BaseFirestore(tcol, tdoc);

  const params = _params();
  const querys = _querys();

  await Firebase.initialize();

  const FirestoreRef = FirestoreBase.getCollection(params);
  const _queryCompositeFilterConstraint = querys.filter((i) => i instanceof QueryCompositeFilterConstraint).at(0);
  const _queryNonFilterConstraint = querys.filter(
    (i) =>
      i instanceof QueryOrderByConstraint ||
      i instanceof QueryLimitConstraint ||
      i instanceof QueryStartAtConstraint ||
      i instanceof QueryEndAtConstraint
  );

  const FirestoreQuery = _queryCompositeFilterConstraint
    ? query(
        FirestoreRef,
        _queryCompositeFilterConstraint as QueryCompositeFilterConstraint,
        ...(_queryNonFilterConstraint as QueryNonFilterConstraint[])
      )
    : query(FirestoreRef, ...(querys as QueryConstraint[]));
  const docs = await getDocs(FirestoreQuery);

  const datas = docs.docs.map((i) => {
    return new service(i.ref, i.data());
  });

  await Promise.all(datas.map((i) => i.load()));
  return datas;
}

export function useFirestoreDoc<
  TCol extends BaseFirestorePath,
  TDoc extends BaseFirestorePath,
  TServiceRequiredMethods extends {
    load(): Promise<void>;
    reload(data: TDoc["class"]["prototype"]): Promise<void>;
  },
  TService extends {
    new (
      docRef: DocumentReference<TDoc["class"]["prototype"], TDoc["class"]["prototype"]>,
      data: TDoc["class"]["prototype"]
    ): InstanceType<TService> & TServiceRequiredMethods;
  },
>(
  tcol: TCol,
  tdoc: TDoc,
  service: TService,
  _params: () => TDoc["param"]["prototype"]
): Ref<UnwrapNestedRefs<InstanceType<TService> | null>> {
  type TServiceInstance = InstanceType<TService> & TServiceRequiredMethods;

  const FirestoreBase = new BaseFirestore(tcol, tdoc);

  const docData: Ref<UnwrapNestedRefs<TServiceInstance | null>> = ref(null);
  let cacheData: TServiceInstance | null = null;

  let unwatch: Unsubscribe;

  let oldParams: TCol["param"]["prototype"];

  const load = async () => {
    const params = _params();

    if (isEqual(oldParams, params)) {
      return;
    }
    if (unwatch) {
      unwatch();
    }

    docData.value = null; // 一度空にしないと、data が変わらないので、コンポーネントの先のデータが更新されない

    oldParams = params;
    cacheData = null;

    await Firebase.initialize();
    const FirestoreRef = FirestoreBase.getDocument(params);
    unwatch = onSnapshot(FirestoreRef, async (querySnapshot) => {
      const data = querySnapshot.data();
      if (data) {
        if (cacheData) {
          // データが更新されたとき
          await cacheData.reload(data);
        } else {
          // 初めてデータが来たとき
          // cacheData = new service(querySnapshot.ref, data);
          // await cacheData.load();
          // どう考えてもvue-tscが頭悪いだけだけど、代入した直後でもnullかもとか言い出すので、linter用に一旦変数に入れる
          const _cacheData = new service(querySnapshot.ref, data);
          cacheData = _cacheData;
          await _cacheData.load();
        }
      } else {
        // 後片付け
        cacheData = null;
      }
      docData.value = null;
      if (cacheData) {
        docData.value = reactive(cacheData);
      }
    });
  };

  onMounted(load);
  onUpdated(load);

  onUnmounted(() => {
    if (unwatch) {
      unwatch();
    }
  });

  return docData;
}

export type useFirestoreColPaginateReturnType<T extends readonly [any, any, any]> = ReturnType<
  typeof useFirestoreColPaginate<T[0], T[1], any, T[2]>
>;
export type useFirestoreColPaginateArgsType<T extends readonly [any, any, any]> = ArgumentsType<
  typeof useFirestoreColPaginate<T[0], T[1], any, T[2]>
>;
export function useFirestoreColPaginate<
  TCol extends BaseFirestorePath,
  TDoc extends BaseFirestorePath,
  TServiceRequiredMethods extends {
    load(): Promise<void>;
    reload(data: TDoc["class"]["prototype"]): Promise<void>;
    data: Ref<TDoc["class"]["prototype"]>;
    docRef: DocumentReference<TDoc["class"]["prototype"], TDoc["class"]["prototype"]>;
  },
  TService extends {
    new (
      docRef: DocumentReference<TDoc["class"]["prototype"], TDoc["class"]["prototype"]>,
      data: TDoc["class"]["prototype"]
    ): InstanceType<TService> & TServiceRequiredMethods;
  },
>(
  tcol: TCol,
  tdoc: TDoc,
  service: TService,
  _params: () => TCol["param"]["prototype"],
  _querys: () => (QueryConstraint | QueryFilterConstraint)[] = () => [],
  perPage = 10,
  _order?: () => { key: string; dir: OrderByDirection }[]
): {
  nowLoading: Ref<boolean>;
  canNext: ComputedRef<boolean>;
  items: Ref<UnwrapNestedRefs<InstanceType<TService>>[]>;
  reload: () => Promise<void>;
  loadNext: () => Promise<void>;
  loadAll: () => Promise<void>;
  hasNext: ComputedRef<boolean>;
} {
  // ソートルール関係
  const getSortKey = () => {
    const ret = _order?.() ?? [];
    if (ret.every((v) => v.key != tcol.primaryKey && v.key != "updatedAt" && v.key != "createdAt")) {
      ret.push({ key: tcol.primaryKey, dir: "desc" });
    }
    return ret;
  };

  // 内部で使用するやつ
  let initialized = false;
  let oldParams: TCol["param"]["prototype"] | null = null;
  let oldQuerys: (QueryConstraint | QueryFilterConstraint)[] | null = null;
  let oldSortKeys: { key: string; dir: OrderByDirection }[] | null = null;
  let sortKeys = getSortKey();

  let unwatch: Unsubscribe | null = null;

  // 外に出す用のデータ
  const _nowLoading = ref(true);
  const cacheData: Ref<{
    [key: DocumentReference["path"]]: InstanceType<TService> & TServiceRequiredMethods;
  }> = ref({});
  const latestData: Ref<{ data: { [key: string]: any } } | null> = ref(null);
  const hasDatas = ref<boolean>(true);
  const items = computed(() => {
    return _(cacheData.value)
      .values()
      .orderBy(
        sortKeys.map((v) => `data.${v.key}`),
        sortKeys.map((v) => v.dir)
      )
      .uniqBy((i) => i.docRef.path)
      .value();
  });
  const canNext = computed(() => !_nowLoading.value && hasDatas.value);
  const hasNext = computed(() => hasDatas.value);

  // 読み込み処理
  const load = async () => {
    try {
      _nowLoading.value = true;

      const params = _params();
      const querys = _querys();
      sortKeys = getSortKey();
      if (isEqual(oldParams, params) && isEqual(oldQuerys, querys) && isEqual(oldSortKeys, sortKeys)) {
        return;
      }
      if (unwatch) {
        unwatch();
      }
      initialized = false;
      oldParams = params;
      oldQuerys = cloneDeep(querys);
      oldSortKeys = cloneDeep(sortKeys);
      hasDatas.value = true;
      cacheData.value = {};
      latestData.value = null;

      querys.push(orderBy("updatedAt", "desc"));
      querys.push(limit(perPage));
      const FirestoreQuery = constraintsToQuery(tcol, tdoc, params, querys);

      /// ソート順的に左が後側の時 1, ソート順的に右が後側の時 -1
      const compare = (left: any, right: any) => {
        for (const { key, dir } of sortKeys) {
          if (dir == "asc") {
            if (left[key] > right[key]) {
              return 1;
            } else if (left[key] < right[key]) {
              return -1;
            }
          } else {
            if (left[key] < right[key]) {
              return 1;
            } else if (left[key] > right[key]) {
              return -1;
            }
          }
        }
        return 0;
      };

      unwatch = onSnapshot(FirestoreQuery, async (querySnapshot) => {
        const lastItem = latestData.value;
        if (lastItem == null) return false;
        if (!initialized) return false;
        const changes = querySnapshot
          .docChanges()
          .map((v) => ({ type: v.type == "removed" ? ("removed" as const) : ("added" as const), doc: v.doc }));

        for (const change of changes) {
          const data = change.doc.data() as any;
          // 未読み込み領域にデータが移動したときは一旦削除して未読み込み状態にする
          // ABCDEFGのうちABCを読み込み済み -> ACDEFGBに変動 ACBになるとDEFGが間になって無限スクロールがおかしくなるので
          if (lastItem != null && compare(data, lastItem.data) > 0) {
            change.type = "removed";
          }
          // deletedAtが追加されたときは非表示
          if (change.doc.exists() && change.doc.data().deletedAt) {
            change.type = "removed";
          }
        }

        for (const change of changes) {
          if (change.type === "removed") {
            // 値が変わってないのにremovedの場合は、limitを超えただけのはずなので削除しない
            const data = change.doc.data() as any;
            if (data && cacheData.value[change.doc.ref.path]) {
              const cache = await getDocFromCache(doc(Firebase.firestore, change.doc.ref.path).withConverter(converter));
              const cacheData = cache.data();
              if(isEqual(cacheData, data)){
                change.type = "added";
              }
            }
          }
        }

        addData(...changes);
      });
    } catch (e) {
      console.error(e);
      throw e;
    } finally {
      _nowLoading.value = false;
    }
  };

  // データの追加
  const addData = async (
    ...snapshots: {
      type: "added" | "removed";
      doc: QueryDocumentSnapshot<TCol["class"]["prototype"], TCol["class"]["prototype"]>;
    }[]
  ) => {
    const promises: Promise<void>[] = [];

    const changes = _(snapshots)
      .groupBy((v) => v.doc.ref.path)
      .mapValues((v) => maxBy(v, (v2) => v2.doc.data().updatedAt))
      .map((v) => v)
      .filter((v): v is NonNullable<typeof v> => !!v)
      .value();
    for (const change of changes) {
      switch (change.type) {
        case "added":
          if (!cacheData.value[change.doc.ref.path]) {
            cacheData.value[change.doc.ref.path] = new service(change.doc.ref, change.doc.data());
            promises.push(cacheData.value[change.doc.ref.path].load());
          } else {
            promises.push(cacheData.value[change.doc.ref.path].reload(change.doc.data()));
          }
          break;
        case "removed":
          hasDatas.value = true;
          delete cacheData.value[change.doc.ref.path];
          break;
      }
    }

    await Promise.all(promises);
    if (Object.keys(cacheData.value).length > 0) {
      latestData.value =
        _(cacheData.value as any)
          .values()
          .orderBy(
            sortKeys.map((v) => `data.${v.key}`),
            sortKeys.map((v) => v.dir)
          )
          .value()
          .at(-1) ?? null;
    } else {
      latestData.value = null;
    }
  };

  // 次の読み込み
  const loadNext = async (perPage: number) => {
    if (_nowLoading.value) {
      return;
    }
    try {
      _nowLoading.value = true;

      const params = _params();
      const querys = _querys();
      const lastItem = latestData.value;

      for (let i = 0; i < sortKeys.length; i++) {
        const { key, dir } = sortKeys[i];
        querys.push(orderBy(key, dir));
        if (lastItem) {
          const startAfter = (lastItem.data as any)[key];
          if (startAfter) {
            let op: WhereFilterOp;
            if (i == sortKeys.length - 1) {
              op = dir === "desc" ? "<" : ">";
            } else {
              op = dir === "desc" ? "<=" : ">=";
            }

            if (DateTime.isDateTime(startAfter)) {
              querys.push(where(key, op, startAfter.toJSDate()));
            } else {
              querys.push(where(key, op, startAfter));
            }
          }
        }
      }

      if (perPage) {
        querys.push(limit(perPage));
      }

      const FirestoreQuery = constraintsToQuery(tcol, tdoc, params, querys);
      const docs = await getDocs(FirestoreQuery);
      let changes = docs.docChanges().map((v) => ({ type: "added" as "added" | "removed", doc: v.doc }));

      await addData(...changes);

      hasDatas.value = docs.size == perPage;
      initialized = true;
    } catch (e) {
      console.error(e);
      hasDatas.value = false;
    } finally {
      _nowLoading.value = false;
    }
  };

  // 全読み込み
  const loadAll = async () => {
    while (hasNext.value) {
      await loadNext(1000);
    }
  };

  onMounted(load);
  onUpdated(load);

  onUnmounted(() => {
    if (unwatch) {
      unwatch();
    }
  });

  return {
    items: items as any,
    reload: load,
    loadNext: () => loadNext(perPage),
    loadAll: loadAll,
    nowLoading: _nowLoading,
    hasNext: hasNext,
    canNext: canNext,
  };
}

export function useFirestoreCol<
  TCol extends BaseFirestorePath,
  TDoc extends BaseFirestorePath,
  TServiceRequiredMethods extends {
    load(): Promise<void>;
    reload(data: TDoc["class"]["prototype"]): Promise<void>;
  },
  TService extends {
    new (
      docRef: DocumentReference<TDoc["class"]["prototype"], TDoc["class"]["prototype"]>,
      data: TDoc["class"]["prototype"]
    ): InstanceType<TService> & TServiceRequiredMethods;
  },
>(
  tcol: TCol,
  tdoc: TDoc,
  service: TService,
  _params: () => TCol["param"]["prototype"],
  _querys: () => (QueryConstraint | QueryFilterConstraint)[] = () => []
): {
  items: Ref<UnwrapNestedRefs<InstanceType<TService>>[]>;
} {
  type TServiceInstance = InstanceType<TService> & TServiceRequiredMethods;

  const FirestoreBase = new BaseFirestore(tcol, tdoc);

  const colData: Ref<UnwrapNestedRefs<TServiceInstance>[]> = ref([]);
  let cacheData: {
    [key: DocumentReference["path"]]: TServiceInstance;
  } = {};

  let unwatch: Unsubscribe;

  let oldParams: TCol["param"]["prototype"];
  let oldQuerys: (QueryConstraint | QueryFilterConstraint)[];

  const load = async () => {
    const params = _params();
    const querys = _querys();

    if (isEqual(oldParams, params) && isEqual(oldQuerys, querys)) {
      return;
    }
    if (unwatch) {
      unwatch();
    }
    oldParams = params;
    oldQuerys = querys;

    colData.value = [];
    cacheData = {};

    const FirestoreRef = FirestoreBase.getCollection(params);
    const _queryCompositeFilterConstraint = querys.filter((i) => i instanceof QueryCompositeFilterConstraint).at(0);
    const _queryNonFilterConstraint = querys.filter(
      (i) =>
        i instanceof QueryOrderByConstraint ||
        i instanceof QueryLimitConstraint ||
        i instanceof QueryStartAtConstraint ||
        i instanceof QueryEndAtConstraint
    );

    const FirestoreQuery = _queryCompositeFilterConstraint
      ? query(
          FirestoreRef,
          _queryCompositeFilterConstraint as QueryCompositeFilterConstraint,
          ...(_queryNonFilterConstraint as QueryNonFilterConstraint[])
        )
      : query(FirestoreRef, ...(querys as QueryConstraint[]));

    try {
      await Firebase.initialize();
      unwatch = onSnapshot(FirestoreQuery, async (querySnapshot) => {
        querySnapshot.docChanges().forEach(async (change) => {
          switch (change.type) {
            case "added":
              cacheData[change.doc.ref.path] = new service(change.doc.ref, change.doc.data());
              await cacheData[change.doc.ref.path].load();
              break;
            case "removed":
              delete cacheData[change.doc.ref.path];
              break;
            case "modified":
              await cacheData[change.doc.ref.path].reload(change.doc.data());
              break;
          }
        });

        colData.value = querySnapshot.docs.map((i) => cacheData[i.ref.path]).map((i) => reactive(i));
      });
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  onMounted(load);
  onUpdated(load);

  onUnmounted(() => {
    if (unwatch) {
      unwatch();
    }
  });

  return {
    items: colData,
  };
}

// Storage
const storageDlURLCache: {
  [key: string]: string;
} = {};
export async function getDownloadURLWithCache(ref: StorageReference) {
  const path = ref.fullPath;
  if (storageDlURLCache[path]) {
    return storageDlURLCache[path];
  }
  const url = await getDownloadURL(ref);
  storageDlURLCache[path] = url;
  return url;
}

export async function setDownloadURLCache(path: string, url: string) {
  storageDlURLCache[path] = url;
}

export async function removeDownloadURLCache(path: string) {
  if (storageDlURLCache[path]) {
    delete storageDlURLCache[path];
  }
}
